import { useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield, ArrowLeft, Lock, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Props { children: ReactNode }

const AdminGate = ({ children }: Props) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [hasRole, setHasRole] = useState<boolean | null>(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHasRole(false);
      return;
    }

    let active = true;
    setHasRole(null);
    void (async () => {
      try {
        // The Edge Function validates the Supabase JWT and checks user_roles
        // with server-side credentials. Never grant access based on client data.
        const { data, error } = await supabase.functions.invoke("admin-auth");
        if (error) throw error;
        if (active) setHasRole(data?.ok === true);
      } catch (error) {
        console.error("Server-side admin verification failed:", error);
        if (active) setHasRole(false);
      }
    })();

    return () => { active = false; };
  }, [user, authLoading]);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail.toLowerCase().trim(),
        password: loginPassword,
      });
      if (error) throw error;
      toast.success("Signed in. Verifying administrator access...");
    } catch (error: any) {
      toast.error(error.message || "Login failed. Check your credentials.");
    } finally {
      setLoginBusy(false);
    }
  };

  if (authLoading || (user && hasRole === null)) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-primary" /></div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <form onSubmit={handleAdminLogin} className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-6 shadow-elevated">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Shield size={28} /></div>
            <h1 className="mt-3 font-display text-xl font-bold">Admin Portal</h1>
            <p className="text-xs text-muted-foreground mt-1">Sign in with your administrator account to continue.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Admin Email</Label>
            <div className="relative"><Input type="email" required placeholder="Admin email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} autoComplete="username" className="pl-9 text-xs" /><Mail className="absolute left-3 top-2.5 text-muted-foreground" size={14} /></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Password</Label>
            <div className="relative"><Input type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} autoComplete="current-password" className="pl-9 text-xs" /><Lock className="absolute left-3 top-2.5 text-muted-foreground" size={14} /></div>
          </div>
          <Button type="submit" disabled={loginBusy} className="w-full h-10 font-semibold gap-2">{loginBusy ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}{loginBusy ? "Signing in..." : "Log in to Admin Panel"}</Button>
          <div className="pt-2 text-center"><button type="button" onClick={() => navigate("/")} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft size={12} /> Return to Home</button></div>
        </form>
      </div>
    );
  }

  if (hasRole !== true) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center bg-background">
        <Shield size={44} className="text-destructive" />
        <h1 className="font-display text-xl font-bold">Admin Privileges Required</h1>
        <p className="text-sm text-muted-foreground max-w-sm">Signed in as <span className="font-semibold text-foreground">{user.email}</span>, but server-side verification did not grant administrator access.</p>
        <div className="flex gap-3"><Button variant="outline" onClick={async () => { await supabase.auth.signOut(); window.location.reload(); }}>Sign Out &amp; Switch Account</Button><Button onClick={() => navigate("/")}>Go Home</Button></div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminGate;
